/**
 * Sidebar — top-level navigation.
 *
 * Four standard sections, with two conditionally-visible:
 *   - AI Analyst (visible only if user.has_ai_analyst)
 *   - User Management (visible only if user.is_admin)
 *
 * Uses react-router for navigation; `useLocation` to know which item is active.
 */
import { CalendarDays, ClipboardList, LayoutGrid, Settings2, Sparkles, Users } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";

interface Props {
  clientName: string;
}

type LucideIcon = typeof LayoutGrid;

interface NavItem {
  path: string;
  label: string;
  Icon: LucideIcon;
  /** If true, only render when the user is an admin. */
  adminOnly?: boolean;
  /** If true, only render when the user has AI Analyst access. */
  aiAnalystOnly?: boolean;
  assumptionsOnly?: boolean;
  eventViewerOnly?: boolean;
}

const ITEMS: NavItem[] = [
  { path: "/dashboards", label: "Dashboards", Icon: LayoutGrid },
  { path: "/data", label: "Data Entry", Icon: ClipboardList, assumptionsOnly: true },
  { path: "/events", label: "Events", Icon: CalendarDays, eventViewerOnly: true },
  { path: "/ai", label: "AI Analyst", Icon: Sparkles, aiAnalystOnly: true },
  { path: "/settings", label: "Settings", Icon: Settings2 },
  { path: "/admin/users", label: "User Management", Icon: Users, adminOnly: true },
];

export function Sidebar({ clientName }: Props) {
  const location = useLocation();
  const { user } = useAuth();

  // Filter items the user doesn't have access to. Both gates fail-closed:
  // if user is null (still loading) or the flag is false, hide the item.
  const visibleItems = ITEMS.filter((item) => {
    if (item.adminOnly && !user?.is_admin) return false;
    if (item.aiAnalystOnly && !user?.has_ai_analyst) return false;
    if (item.assumptionsOnly && !user?.has_assumptions) return false;
    if (item.eventViewerOnly && !user?.has_event_viewer) return false;
    return true;
  });

  return (
    <aside
      className="w-[240px] shrink-0 h-screen sticky top-0 border-r hairline flex flex-col"
      style={{ background: "var(--bg)" }}
    >
      {/* Logo / brand */}
      <div className="px-5 pt-5 pb-5 flex items-center gap-2.5">
        <img
          src="/ashi-logo-v2.webp"
          alt="All States Home Improvement"
          className="h-10 w-10 shrink-0 object-contain"
        />
        <div className="leading-tight">
          <div
            className="font-mono text-[10px] uppercase tracking-[0.16em] font-semibold"
            style={{ color: "var(--ink-3)" }}
          >
            All States
          </div>
          <div
            className="font-serif text-[18px] leading-none tracking-tight mt-0.5"
            style={{ color: "var(--ink)" }}
            title={clientName}
          >
            Analyst Portal
          </div>
        </div>
      </div>

      {/* Nav items */}
      <nav className="px-3 flex flex-col gap-0.5">
        {visibleItems.map(({ path, label, Icon }) => {
          // Active if current URL starts with this item's path.
          // Use startsWith so e.g. /admin/users matches both itself and any
          // nested admin routes we add later.
          const isActive =
            location.pathname === path ||
            (path !== "/" && location.pathname.startsWith(path + "/"));

          return (
            <Link
              key={path}
              to={path}
              className="flex items-center gap-2.5 px-3 py-2 rounded-md text-left t-base focus-ring no-underline"
              style={{
                background: isActive ? "var(--accent-soft)" : "transparent",
                color: isActive ? "var(--accent-ink)" : "var(--ink-2)",
                fontWeight: isActive ? 500 : 400,
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = "var(--hover)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = "transparent";
                }
              }}
            >
              <Icon size={16} strokeWidth={1.5} />
              <span className="text-[14px] font-serif">{label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Footer */}
      <div
        className="px-5 py-4 border-t hairline text-[10px] font-mono uppercase tracking-[0.14em]"
        style={{ color: "var(--ink-4)" }}
      >
        v0.1 · Capitol Data Analytics
      </div>
    </aside>
  );
}
